import i18n, { type PostProcessorModule } from "i18next";
import Backend from "i18next-http-backend";
import { initReactI18next } from "react-i18next";

const FALLBACK_LNG = "en";
const KEY_SEPARATOR = "$$$";

// Non-English locale files come from a Phrase sync pipeline that frequently
// leaves keys missing, or exports an untranslated key with its own name as
// the value. i18next's built-in fallbackLng only covers a key that's fully
// absent from the active locale; it can't tell a real translation from a
// value that just echoes the key. Because `returnObjects: true` makes
// i18next post-process each leaf of the `{ message }` shape individually
// (value is the leaf string, key is `<translationKey>$$$message`), this
// postProcessor strips that suffix, and whenever the resolved value is
// empty or equal to the raw key, swaps in the English message instead — no
// locale file needs to be touched to fix it.
export const englishFallbackPostProcessor: PostProcessorModule = {
  type: "postProcessor",
  name: "englishFallback",
  process(value: any, keys: any, _options: any, translator: any): any {
    const compoundKey = Array.isArray(keys) ? keys[0] : keys;
    const separatorIndex =
      typeof compoundKey === "string" ? compoundKey.indexOf(KEY_SEPARATOR) : -1;
    const baseKey =
      separatorIndex >= 0 ? compoundKey.slice(0, separatorIndex) : compoundKey;

    if (value !== baseKey) return value;

    const enBundle = translator?.resourceStore?.getResourceBundle(
      FALLBACK_LNG,
      "translation"
    );
    const enEntry = enBundle?.[baseKey];
    const enMessage =
      enEntry && typeof enEntry === "object" ? enEntry.message : enEntry;

    return enMessage || "";
  },
};

i18n
  .use(englishFallbackPostProcessor)
  .use(initReactI18next)
  .use(Backend)
  .init({
    fallbackLng: FALLBACK_LNG,
    returnObjects: true,
    keySeparator: "$$$",
    nsSeparator: ":::",
    debug: false,
    postProcess: ["englishFallback"],
    backend: {
      // for all available options read the backend's repository readme file
      loadPath: "/locales/{{lng}}/translation.json",
    },
    interpolation: {
      escapeValue: false,
    },
  })
  .catch((error) => {
    console.error("Error Loading i18", error);
  });

export default i18n;
